/**
 * Material Request detail slide-over.
 *
 * Sections:
 *   1. Hero — title + status pill + total + created/approved by
 *   2. Audit — who created / approved / edited + timestamps
 *   3. Rejection note (when rejected)
 *   4. Items — per-row card with name/brand/qty/rate/total +
 *      delivery status picker (changes one item at a time)
 *   5. Footer actions — Edit / Approve / Reject / Resubmit / Delete
 */
import { useEffect, useMemo, useState } from 'react';

import { canApproveMaterial, isAdminish } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { MaterialRequestFormSlideOver } from '@/components/MaterialRequestFormSlideOver';
import {
  DELIVERY_STATUSES,
  approveRequest,
  deleteMaterialRequest,
  getDeliveryStatusLabel,
  rejectRequest,
  updateItemDeliveryStatus,
  type DeliveryStatus,
  type MaterialRequest,
  type MaterialRequestItem,
} from '@/lib/materialRequests';
import { getCategoryConfig } from '@/lib/materialLibrary';
import type { RoleKey } from '@/lib/useProjectMembers';

function fmtDateTime(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function MaterialRequestDetailSlideOver({
  open,
  onClose,
  request,
  callerRole = 'viewer',
}: {
  open: boolean;
  onClose: () => void;
  request: MaterialRequest | null;
  callerRole?: RoleKey;
}) {
  const { user } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [resubmitMode, setResubmitMode] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState<null | 'approve' | 'reject' | 'delete' | 'delivery'>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEditOpen(false);
      setRejecting(false);
      setRejectNote('');
      setConfirmDelete(false);
      setError(null);
      setBusy(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !editOpen) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, editOpen]);

  const canApprove = canApproveMaterial(callerRole);
  const canDelete = isAdminish(callerRole);
  const isOwner = request?.createdBy === user?.uid;

  const onApprove = async () => {
    if (!user || !request) return;
    setBusy('approve');
    setError(null);
    try {
      await approveRequest(request.id, user.uid);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const onReject = async () => {
    if (!user || !request) return;
    if (rejectNote.trim().length < 3) {
      setError('Reason must be at least 3 characters.');
      return;
    }
    setBusy('reject');
    setError(null);
    try {
      await rejectRequest(request.id, user.uid, rejectNote.trim());
      setRejecting(false);
      setRejectNote('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async () => {
    if (!request) return;
    setBusy('delete');
    setError(null);
    try {
      await deleteMaterialRequest(request.id);
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(null);
    }
  };

  const onDelivery = async (itemIndex: number, next: DeliveryStatus) => {
    if (!user || !request) return;
    setBusy('delivery');
    setError(null);
    try {
      await updateItemDeliveryStatus(request.id, itemIndex, next, user.uid);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const status = request?.status ?? 'pending';
  const totalReceived = useMemo(() => {
    if (!request) return 0;
    return request.items.filter((i) => i.deliveryStatus === 'received_at_site').length;
  }, [request]);

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={onClose}
      />
      <div className={`slide-over${open ? ' open' : ''}`}>
        <div className="slide-header">
          <h2 className="slide-title">
            <i className="ph ph-list-checks" /> Material Request
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body" style={{ paddingBottom: 100 }}>
          {!request ? (
            <div
              style={{
                padding: 32,
                textAlign: 'center',
                color: 'var(--text-tertiary)',
                fontSize: 13,
              }}
            >
              Request not found.
            </div>
          ) : (
            <>
              {/* Hero */}
              <div className="card" style={{ padding: 16 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 12,
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      color: 'var(--text-primary)',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {request.title}
                  </div>
                  <span className={`mat-status-pill ${status}`}>{status}</span>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <span>
                    <i className="ph ph-list-numbers" /> {request.items.length}{' '}
                    item{request.items.length === 1 ? '' : 's'}
                  </span>
                  <span>
                    <i className="ph ph-package" /> {totalReceived}/{request.items.length} received
                  </span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontWeight: 700,
                      color: 'var(--text-primary)',
                      fontSize: 14,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    ₹{Math.round(request.totalValue).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              {/* Audit */}
              <div>
                <SectionTitle>Audit</SectionTitle>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
                  <Audit
                    icon="ph ph-plus-circle"
                    label="Created"
                    value={`${fmtDateTime(request.createdAt?.toMillis?.() ?? null)}`}
                  />
                  {request.approvedAt ? (
                    <Audit
                      icon="ph ph-check-circle"
                      label={request.autoApproved ? 'Auto-approved' : 'Approved'}
                      value={fmtDateTime(request.approvedAt.toMillis())}
                    />
                  ) : null}
                  {request.rejectedAt ? (
                    <Audit
                      icon="ph ph-x-circle"
                      label="Rejected"
                      value={fmtDateTime(request.rejectedAt.toMillis())}
                    />
                  ) : null}
                  {request.editedAt ? (
                    <Audit
                      icon="ph ph-pencil-simple"
                      label="Last edited"
                      value={fmtDateTime(request.editedAt.toMillis())}
                    />
                  ) : null}
                </div>
              </div>

              {/* Rejection note */}
              {request.rejectionNote ? (
                <div
                  style={{
                    background: 'var(--critical-bg)',
                    color: 'var(--critical-text)',
                    padding: 12,
                    borderRadius: 8,
                    fontSize: 13,
                    lineHeight: 1.5,
                    border: '1px solid var(--critical)',
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                      marginBottom: 4,
                    }}
                  >
                    REJECTION REASON
                  </div>
                  {request.rejectionNote}
                </div>
              ) : null}

              {/* Items */}
              <div>
                <SectionTitle>Items · {request.items.length}</SectionTitle>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {request.items.map((it, i) => (
                    <ItemDetail
                      key={i}
                      item={it}
                      canEditDelivery={status === 'approved'}
                      busy={busy === 'delivery'}
                      onDelivery={(next) => onDelivery(i, next)}
                    />
                  ))}
                </div>
              </div>

              {/* Error */}
              {error ? (
                <div
                  style={{
                    background: 'var(--critical-bg)',
                    color: 'var(--critical-text)',
                    padding: 10,
                    borderRadius: 6,
                    fontSize: 13,
                  }}
                >
                  {error}
                </div>
              ) : null}

              {/* Reject form */}
              {rejecting ? (
                <div
                  style={{
                    padding: 12,
                    border: '1px solid var(--critical)',
                    borderRadius: 8,
                    background: 'var(--critical-bg)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'var(--critical-text)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                    }}
                  >
                    Why is this rejected?
                  </div>
                  <textarea
                    style={{
                      width: '100%',
                      minHeight: 70,
                      padding: '8px 10px',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      fontSize: 13,
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.currentTarget.value)}
                    placeholder="e.g. Wrong brand picked, please use Greenply BWR."
                    autoFocus
                  />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        setRejecting(false);
                        setRejectNote('');
                      }}
                      disabled={busy !== null}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      style={{
                        background: 'var(--critical)',
                        borderColor: 'var(--critical)',
                      }}
                      onClick={onReject}
                      disabled={busy !== null}
                    >
                      {busy === 'reject' ? 'Rejecting…' : 'Confirm reject'}
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Sticky footer */}
        {request ? (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              background: '#fff',
              borderTop: '1px solid var(--border)',
              padding: 12,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              boxShadow: '0 -4px 12px rgba(0,0,0,0.04)',
            }}
          >
            {canDelete ? (
              confirmDelete ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--critical)' }}>
                    Delete?
                  </span>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setConfirmDelete(false)}
                    disabled={busy !== null}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    style={{
                      background: 'var(--critical)',
                      borderColor: 'var(--critical)',
                    }}
                    onClick={onDelete}
                    disabled={busy !== null}
                  >
                    Yes
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy !== null}
                  style={{ color: 'var(--critical)' }}
                >
                  <i className="ph ph-trash" /> Delete
                </button>
              )
            ) : null}
            <div style={{ flex: 1 }} />
            {status === 'rejected' && isOwner ? (
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  setResubmitMode(true);
                  setEditOpen(true);
                }}
                disabled={busy !== null}
              >
                <i className="ph-bold ph-arrow-clockwise" /> Resubmit
              </button>
            ) : null}
            {status !== 'rejected' && canApprove ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setResubmitMode(false);
                  setEditOpen(true);
                }}
                disabled={busy !== null}
              >
                <i className="ph ph-pencil-simple" /> Edit
              </button>
            ) : null}
            {status === 'pending' && canApprove ? (
              <>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setRejecting(true)}
                  disabled={busy !== null}
                  style={{ color: 'var(--critical)' }}
                >
                  <i className="ph ph-x" /> Reject
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  style={{
                    background: 'var(--success)',
                    borderColor: 'var(--success)',
                  }}
                  onClick={onApprove}
                  disabled={busy !== null}
                >
                  <i className="ph-bold ph-check" />{' '}
                  {busy === 'approve' ? 'Approving…' : 'Approve'}
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Edit form layered on top */}
      <MaterialRequestFormSlideOver
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setResubmitMode(false);
        }}
        orgId={request?.orgId ?? null}
        projectId={request?.projectId ?? ''}
        request={request}
        creatorRole={callerRole}
        resubmit={resubmitMode}
      />
    </>
  );
}

// ───── Helpers ─────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: 8,
        paddingBottom: 6,
        borderBottom: '1px solid var(--border)',
      }}
    >
      {children}
    </div>
  );
}

function Audit({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        color: 'var(--text-secondary)',
      }}
    >
      <i className={icon} style={{ color: 'var(--text-tertiary)' }} />
      <span
        style={{
          fontWeight: 600,
          color: 'var(--text-primary)',
          minWidth: 90,
        }}
      >
        {label}
      </span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

function ItemDetail({
  item,
  canEditDelivery,
  busy,
  onDelivery,
}: {
  item: MaterialRequestItem;
  canEditDelivery: boolean;
  busy: boolean;
  onDelivery: (next: DeliveryStatus) => void;
}) {
  const cfg = item.category ? getCategoryConfig(item.category) : null;
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        background: '#fff',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--text-primary)',
              lineHeight: 1.3,
            }}
          >
            {item.name}
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--text-secondary)',
              marginTop: 2,
            }}
          >
            {[item.brand, item.variety, item.make, item.size]
              .filter(Boolean)
              .join(' · ') || (cfg ? cfg.label : '—')}
          </div>
        </div>
        <div
          style={{
            textAlign: 'right',
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700 }}>
            ₹{Math.round(item.totalCost || 0).toLocaleString('en-IN')}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
            {item.quantity} {item.unit} · ₹{(item.rate || 0).toLocaleString('en-IN')}/{item.unit}
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          borderTop: '1px solid var(--border)',
          paddingTop: 8,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
          }}
        >
          Delivery
        </span>
        {canEditDelivery ? (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {DELIVERY_STATUSES.map((s) => {
              const active = item.deliveryStatus === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  className={`delivery-pill ${s.key}`}
                  onClick={() => onDelivery(s.key)}
                  disabled={busy || active}
                  style={{
                    border: active
                      ? `1.5px solid ${s.color}`
                      : '1px solid transparent',
                    cursor: busy ? 'wait' : active ? 'default' : 'pointer',
                    fontFamily: 'inherit',
                    opacity: busy && !active ? 0.5 : 1,
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        ) : (
          <span
            className={`delivery-pill ${item.deliveryStatus}`}
          >
            {getDeliveryStatusLabel(item.deliveryStatus)}
          </span>
        )}
      </div>
    </div>
  );
}
